import { trackError } from '@local/metrics';
import type { SelectMenuItem } from '@local/web-design-system/components/SelectMenu';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/GridLegacy';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { decodeToken } from 'react-jwt';
import { Navigate, useNavigate } from 'react-router';

import type { Discovery } from '../../../apiClients/entitlementsClient/requests/BentleyEntitlementsAPI';
import { useLoadBentleyLicenseOrgsQuery } from '../../../apiClients/entitlementsClient/requests/BentleyEntitlementsAPI';
import type {
    AccessAndIDTokenWithExpiresAt as AccessToken,
    IdTokenDecoded,
} from '../../../apiClients/types';
import { EVO_APPS, EVO_NEXT_SERVICES, LOGIN_ERROR_ROUTE, LOGIN_ROUTE } from '../../../constants';
import { getCombinedToken as getAccessToken } from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import {
    getDeeplink,
    getHubFromDeeplink,
    getOrgFromDeeplink,
} from '../../../store/sessionStorageHelpers/deeplinkHelper/deeplinkHelper';
import {
    type EvoInstance,
    getCurrentEvoInstance,
    getLastUsedInstance,
    type Hub,
    type Org,
    setCurrentEvoInstance,
    setLastUsedInstance,
} from '../../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { Background } from '../../background/Background';
import { LoginContext } from '../../loginHandler/LoginContext';
import { OrgHubSelectMenu } from '../OrgHubSelectMenu/OrgHubSelectMenu';
import { useStyles } from '../sharedSelector.styles';
import { getEvoInstanceForOrgIdWithOptionalHubCode } from '../utils';

interface OptionsGroup {
    label: string;
    optionIds: string[];
}

const sortByDisplayName = (a: { display_name: string }, b: { display_name: string }) =>
    a.display_name.toLowerCase() > b.display_name.toLowerCase() ? 1 : -1;

// Maps the list of orgs and their related hubs for the selectMenu display.
function evoOptionsGroups(
    data: Discovery | undefined,
    evoMap: Map<string, { org: Org; hub: Hub }>,
): OptionsGroup[] {
    const response: OptionsGroup[] = [];
    if (data) {
        [...data.organizations].sort(sortByDisplayName).forEach((org: Org) => {
            const hubs = [];
            for (const entry of evoMap.entries()) {
                if (entry[1].org.id === org.id) {
                    hubs.push(entry[1].hub);
                }
            }
            if (hubs.length > 0) {
                response.push({
                    label: org.display_name,
                    optionIds: hubs.sort(sortByDisplayName).map((hub) => `${org.id}-${hub.code}`),
                });
            }
        });
    }
    return response;
}

function generateEvoMap(
    data: Discovery | undefined,
    service: string | string[],
): Map<string, { org: Org; hub: Hub }> {
    const map = new Map<string, { org: Org; hub: Hub }>();
    if (data) {
        const hubs = new Map<string, Hub>();
        for (const hub of data.hubs) {
            hubs.set(hub.code, hub);
        }
        const orgs = new Map<string, Org>();
        for (const org of data.organizations) {
            orgs.set(org.id, org);
        }
        const services = Array.isArray(service) ? service : [service];
        for (const sa of data.service_access) {
            const hub = hubs.get(sa.hub_code);
            const org = orgs.get(sa.org_id);
            if (services.some((s) => sa.services.includes(s)) && hub && org) {
                map.set(`${sa.org_id}-${sa.hub_code}`, { org, hub });
            }
        }
    }
    return map;
}

function labelComponent(labelHeader: string, label: string): React.ComponentType<any> {
    return () => (
        <Stack direction="column" alignItems="left" width="320px">
            <Typography variant="body2">{labelHeader}</Typography>
            <Typography variant="body2" sx={{ fontSize: '12px !important' }}>
                {label}
            </Typography>
        </Stack>
    );
}

function instances(data: Map<string, { org: Org; hub: Hub }>): SelectMenuItem[] {
    const items = [];
    for (const entry of data.entries()) {
        items.push({
            key: entry[0],
            label: labelComponent(
                entry[1].org.display_name,
                `${entry[1].hub.display_name} (${entry[1].hub.code})`,
            ),
        });
    }
    return items.sort();
}

function getDecodedIDToken(accessToken: AccessToken | null) {
    if (accessToken && accessToken.id_token) {
        return decodeToken<IdTokenDecoded>(accessToken.id_token);
    }
    return null;
}

const EVO_READY_REFRESH_MS = 2 * 60 * 1000;

function GettingEvoReady() {
    // Reload to re-check entitlements once the new instance should be provisioned.
    useEffect(() => {
        const timer = setTimeout(() => window.location.reload(), EVO_READY_REFRESH_MS);
        return () => clearTimeout(timer);
    }, []);

    return (
        <Stack spacing={1} alignItems="center">
            <Typography variant="body1" align="center">
                We&apos;re getting Evo ready for you.
            </Typography>
            <Typography variant="body2" align="center">
                This might take a few minutes...
            </Typography>
        </Stack>
    );
}

interface Props {
    /** The path (after /orgId/) to redirect a user to after selecting an org. If onSuccess is provided, this param will be ignored */
    homePagePath?: string;
    /** Optional: callback for when an instance has been selected. If provided, homePathPath redirect will not happen. */
    onSuccess?: (instance: EvoInstance) => void;
    evoNextEnabled?: boolean;
}

export function BentleyOrgHubSelector({
    homePagePath = '/',
    evoNextEnabled = false,
    onSuccess,
}: Readonly<Props>) {
    const { service } = useContext(LoginContext);
    const { classes } = useStyles();
    const navigate = useNavigate();
    const accessToken = getAccessToken();
    const { evouiCombineDevIntgInstanc: combineResponses } = useFlags();
    const serviceForQuery = service === 'evo-next' ? EVO_NEXT_SERVICES : service;

    const evoData = useLoadBentleyLicenseOrgsQuery(
        accessToken?.access_token && service !== 'none'
            ? {
                  service: serviceForQuery,
                  ...(combineResponses && EVO_APPS.includes(service) && { combineResponses }),
              }
            : skipToken,
    );
    const evoMap = useMemo(
        () => generateEvoMap(evoData.data, serviceForQuery),
        [evoData.data, serviceForQuery],
    );
    const optionGroups = evoOptionsGroups(evoData.data, evoMap);
    const selected = evoData.data && optionGroups.length ? optionGroups[0].optionIds[0] : '';

    const [selectedInstance, setSelectedInstance] = useState<string>('');
    const [fetchToken, setFetchToken] = useState<boolean>(false);

    useEffect(() => {
        // Only one license, use that one.
        if (evoMap.size === 1 && !fetchToken) {
            const instance = [...evoMap.entries()][0][1];
            continueWithInstance(instance);
        }
    }, [evoMap]);

    const decodedToken = getDecodedIDToken(accessToken);
    const userName = decodedToken ? `${decodedToken?.given_name} ${decodedToken?.family_name}` : '';
    const expiredOrLackingToken =
        !accessToken ||
        (accessToken && accessToken.expires_at <= Date.now() && !accessToken?.refresh_token);

    useEffect(() => {
        const { data, error, isLoading } = evoData;
        if (error) {
            trackError(
                'Error loading instances',
                `Error loading instances for ${service} in <BentleyOrgHubSelector>`,
                {
                    evo: error,
                },
            );
            // If we can't get the list of service instances, return the user to the login page.
            navigate(`${LOGIN_ERROR_ROUTE}?error=entitlements_failure&bentley_token=true`);
            return;
        }
        // Wait for query to resolve before processing data
        if (isLoading) return;
        // No licenses, skip and move on.
        if (data?.service_access.length === 0) {
            if (data?.has_new_instances) {
                return;
            }
            setCurrentEvoInstance({ hasNoLicense: true });
            if (onSuccess) {
                onSuccess(getCurrentEvoInstance());
            } else {
                navigate(homePagePath);
            }
            return;
        }
        // If no data is available after loading, redirect to login.
        if (!data) {
            trackError(
                `Invalid Bentley license scope provided for ${service}`,
                'No valid org/hub for service',
            );
            navigate(`${LOGIN_ERROR_ROUTE}?error=entitlements_failure&bentley_token=true`);
        }
    }, [evoData, evoNextEnabled]);

    const continueWithInstance = useCallback((instance?: EvoInstance) => {
        if (!instance) {
            return;
        }
        setLastUsedInstance(instance.org?.id, instance.hub?.code);

        setCurrentEvoInstance({ org: instance.org, hub: instance.hub });
        setSelectedInstance(`${instance?.org?.id}-${instance?.hub?.code}`);
        setFetchToken(true);
    }, []);

    if (expiredOrLackingToken) {
        // Scenario where user sits on org selector page for 24 hours
        return <Navigate to={LOGIN_ROUTE} />;
    }
    // Skip the org selector when following a deeplink with a valid org id & hub-code in the path.
    const deeplink = getDeeplink(homePagePath);

    const orgFromDeeplink = getOrgFromDeeplink(deeplink, evoData?.data?.organizations);
    if (orgFromDeeplink) {
        const hubFromDeeplink = getHubFromDeeplink(deeplink, evoData?.data?.hubs);
        const evoInstance = getEvoInstanceForOrgIdWithOptionalHubCode(
            orgFromDeeplink.id,
            evoData.data,
            hubFromDeeplink?.code,
        );
        if (!evoInstance) {
            return <Navigate to={LOGIN_ROUTE} />;
        }

        setCurrentEvoInstance(evoInstance);
        return <Navigate to={deeplink} />;
    }
    if (fetchToken && accessToken) {
        const instance = evoMap.get(selectedInstance);
        if (onSuccess && instance) {
            onSuccess(instance);
            return null;
        }

        if (evoNextEnabled) {
            return <Navigate to={homePagePath} />;
        } else if (instance?.org.id) {
            // Pathnames cannot have embedded double slashes - so normalizing homePagePath
            return (
                <Navigate
                    to={`/${instance.org.id}/${homePagePath.startsWith('/') ? homePagePath.substring(1) : homePagePath}`}
                />
            );
        } else {
            // A pokey-case that should be monitored in-case the CX becomes problematic
            trackError(
                'Error loading instance',
                `Selection ${selectedInstance} is missing in <BentleyOrgHubSelector>`,
                { evoMapKeys: Array.from(evoMap.keys()).join(', ') },
            );
            return (
                <Navigate
                    to={`/${homePagePath.startsWith('/') ? homePagePath.substring(1) : homePagePath}`}
                />
            );
        }
    }
    const expired = accessToken && accessToken.expires_at <= Date.now();
    const invalidToken = accessToken === null || Object.keys(accessToken).length === 0;
    if (expired || invalidToken) {
        // Scenario where user sits on org selector page for 24 hours or a pokey-case of having no token
        return <Navigate to={LOGIN_ROUTE} />;
    }

    // Once instances have loaded, we get a lastUsedCookie to potentially skip the org selector page
    const lastUsedInstance = !evoData?.isLoading ? getLastUsedInstance() : undefined;
    if (lastUsedInstance) {
        continueWithInstance(lastUsedInstance);
    }

    const renderInstanceSelector = () => (
        <>
            <div className={classes.label}>
                <Typography>Name: {userName || null}</Typography>
                <Grid item xs className={classes.selectMenu}>
                    <OrgHubSelectMenu
                        options={instances(evoMap)}
                        onSelected={(key) => setSelectedInstance(key)}
                        defaultValue={selected}
                        width={360}
                    />
                </Grid>
            </div>
            <Button
                color="primary"
                variant="contained"
                size="large"
                className={classes.button}
                onClick={() => continueWithInstance(evoMap.get(selectedInstance))}
                disabled={!selectedInstance}
                id="continue-btn"
            >
                Continue
            </Button>
        </>
    );

    const isPreparingEvo =
        evoData.data?.has_new_instances && evoData.data?.service_access.length === 0;

    return evoData?.isLoading ? (
        <Background>
            <Grid
                item
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100px',
                }}
                alignContent="center"
                justifyContent="center"
                display="grid"
            >
                <CircularProgress automation-id="spinner-loading" />
            </Grid>
        </Background>
    ) : (
        <Background>
            <Grid item xs={12} className={classes.formContainer}>
                {isPreparingEvo ? <GettingEvoReady /> : renderInstanceSelector()}
            </Grid>
        </Background>
    );
}
